import { FC, useCallback, useState } from 'react'
import { Link } from 'react-router'
import r from 'routes'

import { CopyButton } from '@/components/global/copy-button'
import LoadingButton from '@/components/loading-button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAPI } from '@/hooks/useAPI'
import { isErrorResponse } from '@/lib/asyncThunk'
import { Branding } from '@/out/branding'
import { updateDomain } from '@/thunks/branding/domain'
import SettingsCard from '@/components/settings-card'
import { Globe } from 'lucide-react'

type Props = {
  onSuccess: (branding: Branding) => void
}

const CustomDomainCard: FC<Props> = ({ onSuccess }) => {
  const [domain, setDomain] = useState('')

  const [error, setError] = useState<string>()
  const [setup, { timer }] = useAPI(updateDomain)
  const handleSubmit = useCallback(async () => {
    try {
      setError(undefined)

      const [branding] = await setup({ domain })
      onSuccess(branding)
    } catch (err) {
      if (isErrorResponse(err) && err.code === 'DNS_INVALID') {
        setError("DNS settings are invalid or haven't propagated yet")
      } else {
        setError('An error occurred while verifying the DNS settings')
      }
    }
  }, [setup, domain, onSuccess])

  return (
    <SettingsCard
      Icon={Globe}
      title="Custom Domain Setup"
      description="Configure your organization's custom domain for a branded experience."
      sections={[
        <section key="step-1">
          <Label>Step 1: Configure the DNS for your subdomain</Label>
          <p className="mt-2 text-sm text-muted-foreground">
            If, for example, you plan to use cx.mycompany.com as the public facing domain, you&apos;ll need to create a
            CNAME record for cx which points to {import.meta.env.VITE_CUSTOMER_PROXY_DOMAIN}.
          </p>

          <div className="flex items-center justify-between gap-2 my-4">
            <div className="p-4 rounded-md bg-primary-foreground">
              <Label className="text-xs uppercase text-muted-foreground">Type</Label>
              <div className="flex items-center gap-2 mt-1">
                <p className="font-mono">CNAME</p>
                <CopyButton text="CNAME" small />
              </div>
            </div>
            <div className="flex-1 p-4 rounded-md bg-primary-foreground">
              <Label className="text-xs uppercase text-muted-foreground">Value</Label>
              <div className="flex items-center gap-2 mt-1">
                <p className="font-mono">{import.meta.env.VITE_CUSTOMER_PROXY_DOMAIN}</p>
                <CopyButton text={import.meta.env.VITE_CUSTOMER_PROXY_DOMAIN} small />
              </div>
            </div>
          </div>
        </section>,

        <section key="step-2">
          <Label>Step 2: Enter your domain</Label>

          <p className="mt-2 mb-4 text-sm text-muted-foreground">
            Once you&apos;ve created the DNS record and you see that the change has been propagated on a site like{' '}
            <a href="https://dnschecker.org/#CNAME" target="_blank" rel="noreferrer" className="underline">
              dnschecker.org
            </a>
            , please enter the domain below.
          </p>

          <Alert className="mb-4 bg-amber-50/80 border-amber-100 text-amber-600">
            <AlertDescription>⚠️ DNS changes can take up to 48 hours to propagate globally.</AlertDescription>
          </Alert>

          <Input
            id="domain"
            placeholder="cx.example.com"
            value={domain}
            autoComplete="one-time-code"
            onChange={e => setDomain(e.target.value)}
            required
          />
        </section>,

        error && (
          <Alert key="error" variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ),
      ].filter(Boolean)}
      footer={
        <>
          <div className="flex items-center text-sm text-muted-foreground">
            Need help?
            <Link
              to={r.support.root}
              className="font-medium transition-colors  text-primary hover:text-primary/80 hover:underline"
            >
              Contact our support team
            </Link>
          </div>

          <LoadingButton
            timer={timer}
            onClick={handleSubmit}
            disabled={!domain.match(/^[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}$/)}
          >
            Verify DNS Settings
          </LoadingButton>
        </>
      }
    />
  )
}

export default CustomDomainCard
